import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import OrderCard from "@/components/OrderCard";
import AutoRefreshIndicator from "@/components/AutoRefreshIndicator";
import PullToRefreshContainer from "@/components/PullToRefreshContainer";

const TABS = [
  { id: "all", label: "Tutti" },
  { id: "active", label: "In corso" },
  { id: "delivered", label: "Consegnati" },
  { id: "cancelled", label: "Annullati" },
];

export default function CustomerOrders() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const { data } = await api.get("/orders/mine");
      setOrders(data);
      setLastUpdated(new Date());
    } catch { /* silent on auto-refresh */ }
    finally {
      setLoading(false);
      if (manual) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Phase 43.6 — auto-refresh ogni 30s per riflettere lo stato aggiornato
    // (es. quando un driver accetta una consegna in attesa).
    const t = setInterval(() => load(false), 30_000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = orders.filter((o) => {
    if (tab === "all") return true;
    if (tab === "active") return !["delivered", "cancelled"].includes(o.status);
    return o.status === tab;
  });

  return (
    <PullToRefreshContainer onRefresh={() => load(true)}>
    <div
      className="space-y-5 max-w-3xl mx-auto w-full overflow-x-clip"
      data-testid="customer-orders-page"
    >
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <p className="overline">Storico</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">I tuoi ordini</h1>
        </div>
        <AutoRefreshIndicator
          lastUpdated={lastUpdated}
          intervalSec={30}
          loading={refreshing}
          onRefresh={() => load(true)}
        />
      </div>

      {/* Phase 43.47 — Tabs: scroll orizzontale soft con compensazione padding parent.
          `-mx-4 px-4` consente di "bleed" oltre il padding dell'AppShell main su mobile,
          dando 1 ulteriore tab di "indizio scroll" senza tagliare il contenuto. */}
      <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto pb-1 no-scrollbar">
        <div className="flex gap-2 w-max sm:w-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`customer-orders-tab-${t.id}`}
              className={`shrink-0 rounded-full px-4 h-10 text-sm font-semibold transition ${
                tab === t.id ? "bg-dapnow-ink text-white" : "bg-white border border-dapnow-line text-dapnow-ink hover:border-dapnow-blue/40"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="card-soft p-10 text-center text-dapnow-muted">Nessun ordine in questa categoria.</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
          {filtered.map((o) => (
            <OrderCard key={o.id} order={o} to={`/app/orders/${o.id}`} testId={`customer-order-${o.id}`} />
          ))}
        </div>
      )}
    </div>
    </PullToRefreshContainer>
  );
}
